/* eslint-disable react/jsx-props-no-spreading */
import AddCircleIcon from "@mui/icons-material/AddCircle"
import { Box, Grid, Stack, TextField, Typography } from "@mui/material"
import {
    GridColDef,
    GridPaginationModel,
    GridRowParams,
} from "@mui/x-data-grid"
import { ReactElement, useEffect, useMemo, useState } from "react"

import { LCTOnlyDatasets } from "../../../constants/enums"
import {
    COMPLIANT,
    DEMAND_COMPLIANCE_STATUS_KW,
    DESC,
    DEVICE_MODEL_FILTER_KW,
    MAX_DEMAND_KW,
    MIN_DEMAND_KW,
    ORDER_BY_KW,
    ORDER_KW,
    TTR_DEFAULT_ORDER_BY,
} from "../../../constants/generalConstants"
import useDemandFilterReferenceData from "../../../services/middleware/hooks/useDemandFilterReference"
import { useSearchDemandDevicesPage } from "../../../services/middleware/hooks/useMultiFetcherSearchDemandDevices"
import {
    DEM_MANUFACTURER_URL_PARAM,
    DEM_PHASE_URL_PARAM,
    DEM_TYPE_URL_PARAM,
} from "../../../services/middleware/mappers/demandFilterReferenceMapper"
import { IDemDatabaseSearchResult } from "../../../services/middleware/mappers/searchDemandDevicesMapper"
import { GRID_SPACING } from "../../../themes/defaultThemeParams"
import AdminDataGrid, {
    CustomAction,
} from "../../dataDisplay/adminDataGrid/adminDataGrid"
import AppControlledSelect from "../../inputs/controlled/appControlledSelect"

export const DEMAND_PANEL_PAGE_SIZE = 20

export type DemandLockedFilters = {
    /** When set, restricts the search to this phase and hides the phase
     * control. */
    phaseId?: string | number
}

export interface DemandDeviceSearchPanelProps {
    /** Which demand dataset to query (HP / EVAC / EVDC). */
    dataset: LCTOnlyDatasets
    /** Restrictions imposed by the caller. */
    locked?: DemandLockedFilters
    /** Fired when the user picks a row. */
    onSelect: (row: IDemDatabaseSearchResult) => void
    /** databaseId of a row to render as visually selected, if any. */
    selectedDatabaseId?: number | null
}

const columns: GridColDef[] = [
    {
        field: "deviceReference",
        headerName: "System Reference",
        flex: 1.4,
        minWidth: 180,
        sortable: false,
    },
    {
        field: "datePublished",
        headerName: "Date Published",
        flex: 1,
        minWidth: 130,
        sortable: false,
    },
    {
        field: "manufacturer",
        headerName: "Manufacturer",
        flex: 1.2,
        sortable: false,
    },
    { field: "modelRef", headerName: "Model", flex: 1.2, sortable: false },
    { field: "type", headerName: "Type", flex: 1.4, sortable: false },
    {
        field: "maxDemand",
        headerName: "Max Demand (A)",
        flex: 1,
        sortable: false,
    },
    { field: "phases", headerName: "Phase", flex: 0.8, sortable: false },
]

/**
 * Reusable panel for searching demand devices (HP / EVAC / EVDC). State is
 * local — no URL sync, no redux. Mirrors {@link GenDeviceSearchPanel}.
 */
const DemandDeviceSearchPanel = ({
    dataset,
    locked,
    onSelect,
    selectedDatabaseId,
}: DemandDeviceSearchPanelProps): ReactElement => {
    const { referenceData, isLoading: referenceLoading } =
        useDemandFilterReferenceData(dataset)

    const [manufacturer, setManufacturer] = useState<string>("")
    const [deviceType, setDeviceType] = useState<string>("")
    const [phase, setPhase] = useState<string>("")
    const [deviceModel, setDeviceModel] = useState<string>("")
    const [demandMin, setDemandMin] = useState<string>("")
    const [demandMax, setDemandMax] = useState<string>("")
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
        {
            page: 0,
            pageSize: DEMAND_PANEL_PAGE_SIZE,
        }
    )

    const lockedPhase = useMemo(
        () =>
            locked?.phaseId !== undefined ? String(locked.phaseId) : undefined,
        [locked?.phaseId]
    )

    const searchParams = useMemo(() => {
        const params = new URLSearchParams()

        // Default ordering: most recently published first. The demand
        // search hook does not auto-default sort, so we set it explicitly
        // here to match the data grid header indicator.
        params.set(ORDER_BY_KW, TTR_DEFAULT_ORDER_BY)
        params.set(ORDER_KW, DESC)

        // Compliance is always locked to "Compliant" — the picker should
        // never offer non-compliant devices.
        params.set(DEMAND_COMPLIANCE_STATUS_KW, COMPLIANT)

        if (lockedPhase !== undefined) {
            params.set(DEM_PHASE_URL_PARAM, lockedPhase)
        } else if (phase) {
            params.set(DEM_PHASE_URL_PARAM, phase)
        }
        if (manufacturer) {
            params.set(DEM_MANUFACTURER_URL_PARAM, manufacturer)
        }
        if (deviceType) {
            params.set(DEM_TYPE_URL_PARAM, deviceType)
        }
        if (deviceModel) {
            params.set(DEVICE_MODEL_FILTER_KW, deviceModel)
        }
        if (demandMin) {
            params.set(MIN_DEMAND_KW, demandMin)
        }
        if (demandMax) {
            params.set(MAX_DEMAND_KW, demandMax)
        }

        return params
    }, [
        lockedPhase,
        manufacturer,
        deviceType,
        phase,
        deviceModel,
        demandMin,
        demandMax,
    ])

    const { data, isLoading } = useSearchDemandDevicesPage(
        searchParams,
        dataset,
        paginationModel.page,
        DEMAND_PANEL_PAGE_SIZE
    )

    const [rowCount, setRowCount] = useState(0)
    useEffect(() => {
        if (data?.totalResults !== undefined) {
            setRowCount(data.totalResults)
        }
    }, [data])

    useEffect(() => {
        setPaginationModel((prev) => ({ ...prev, page: 0 }))
    }, [searchParams])

    const customActions: CustomAction<IDemDatabaseSearchResult>[] = useMemo(
        () => [
            {
                key: "select-demand-device",
                label: "Select",
                icon: (
                    <AddCircleIcon aria-label="select" sx={{ fontSize: 32 }} />
                ),
                onClick: (row: IDemDatabaseSearchResult) => onSelect(row),
                tooltip: "Select this device",
            },
        ],
        [onSelect]
    )

    const restrictionLines: string[] = []
    if (lockedPhase !== undefined) {
        restrictionLines.push(`Phase: ${lockedPhase}`)
    }

    const phaseOptions: string[] = useMemo(() => {
        const phases = referenceData?.phases
        if (Array.isArray(phases)) return phases.map((p) => String(p))
        if (phases !== undefined) return [String(phases)]
        return []
    }, [referenceData])

    const rows = data?.data ?? []

    return (
        <Stack spacing={GRID_SPACING * 2}>
            {restrictionLines.length > 0 && (
                <Typography color="text.secondary">
                    Search restricted to — {restrictionLines.join(" \u00B7 ")}.
                </Typography>
            )}
            <Grid container spacing={GRID_SPACING}>
                <Grid item xs={12} md={4}>
                    <AppControlledSelect
                        fullWidth
                        options={referenceData?.deviceManufacturers ?? []}
                        value={manufacturer || null}
                        loading={referenceLoading}
                        setValue={(_event, value) =>
                            setManufacturer(String(value ?? ""))
                        }
                        label="Manufacturer"
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <AppControlledSelect
                        fullWidth
                        options={referenceData?.deviceTypes ?? []}
                        value={deviceType || null}
                        loading={referenceLoading}
                        setValue={(_event, value) =>
                            setDeviceType(String(value ?? ""))
                        }
                        label="Type"
                    />
                </Grid>
                {lockedPhase === undefined && (
                    <Grid item xs={12} md={4}>
                        <AppControlledSelect
                            fullWidth
                            options={phaseOptions}
                            value={phase || null}
                            loading={referenceLoading}
                            setValue={(_event, value) =>
                                setPhase(String(value ?? ""))
                            }
                            label="Phase"
                        />
                    </Grid>
                )}
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        label="Model"
                        value={deviceModel}
                        onChange={(event) => setDeviceModel(event.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        label="Minimum Max Demand (A)"
                        type="number"
                        value={demandMin}
                        onChange={(event) => setDemandMin(event.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        label="Maximum Max Demand (A)"
                        type="number"
                        value={demandMax}
                        onChange={(event) => setDemandMax(event.target.value)}
                    />
                </Grid>
            </Grid>

            <Box>
                <AdminDataGrid<IDemDatabaseSearchResult>
                    rows={rows}
                    columns={columns}
                    getRowId={(row: IDemDatabaseSearchResult) =>
                        String(row.databaseId)
                    }
                    itemTypeText="device"
                    isLoading={isLoading || referenceLoading}
                    showActions
                    customActions={customActions}
                    hideToolbar
                    showTitle={false}
                    disableRowSelectionOnClick
                    paginationMode="server"
                    rowCount={rowCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[DEMAND_PANEL_PAGE_SIZE]}
                    getRowClassName={(params: GridRowParams) =>
                        selectedDatabaseId !== undefined &&
                        selectedDatabaseId !== null &&
                        params.row.databaseId === selectedDatabaseId
                            ? "row-selected"
                            : ""
                    }
                />
            </Box>
        </Stack>
    )
}

DemandDeviceSearchPanel.defaultProps = {
    locked: undefined,
    selectedDatabaseId: null,
}

export default DemandDeviceSearchPanel
